import {announce} from '@react-aria/live-announcer'
import {DialogTrigger} from 'react-aria-components'

import CategoryIcon from '@/assets/food/icons/category.svg?react'
import CategoryDefault from '@/assets/food/icons/categoryDefault.svg?react'
import PlusIcon from '@/assets/food/icons/plus.svg?react'
import StoreDefault from '@/assets/food/icons/storeDefault.svg?react'
import CloseIcon from '@/assets/icons/close.svg?react'
import SearchIcon from '@/assets/icons/search.svg?react'
import Loading from '@/components/Loading/Loading'
// import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import type {RestaurantDetail} from '@/lib/food-grubhub/foodRestaurantDetailModel'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
// import useGrubhubValidToken from '@/lib/food-grubhub/hooks/useGrubhubValidToken'
import useMenuItemListing from '@/lib/food-grubhub/hooks/useMenuItemListing'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {
  FoodGrubhubDetailRoute,
  // FoodGrubhubMenuItemDetailRoute
} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useSetSearch from '@/utils/router/useSetSearch'
import CategoryItemPrice from '@/views/FoodGrubhubRestaurantDetail/components/CategoryItemPrice'
import {MenuItemDetailDialog} from '@/views/FoodGrubhubRestaurantDetail/components/MenuItemDetailDialog/MenuItemDetailDialog'
import ReadMoreString from '@/views/FoodGrubhubRestaurantDetail/components/ReadMoreString'
import style from '@/views/FoodGrubhubRestaurantDetail/FoodGrubhubRestaurantDetail.module.scss'

const ImageDefaultLoader = ({value}: {value: 'store' | 'category'}) => {
  return (
    <div className={style.StoreImageDefaultContainer}>
      {value === 'store' ? <StoreDefault /> : <CategoryDefault />}
    </div>
  )
}

type DialogState = {type: 'closed'} | {type: 'customize'}

interface Props {
  restaurantDetail?: RestaurantDetail
  restaurantId: string
  operationId: string
}

export default function FoodGrubhubMenuItemSearch({
  restaurantDetail,
  restaurantId,
  operationId,
}: Props) {
  const menuItemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const filterInputRef = useRef<HTMLInputElement | null>(null)
  const {q, menuItemId} = FoodGrubhubDetailRoute.useSearch()
  const [activeIndex, setActiveIndex] = useState('')
  const [filterInput, setFilterInput] = useState(q)
  const router = useRouter()
  const timestampRef = useRef(Date.now())
  const [storageUserLocation] = useGrubhubUserAddress()
  const [dialogState, setDialogState] = useState<DialogState>({type: 'closed'})
  const [selectedMenuItemId, setSelectedMenuItemId] = useState(menuItemId)
  const setMenuItemId = useSetSearch(FoodGrubhubDetailRoute.id, 'menuItemId')

  const {data: authData} = useGrubhubGetAuth()

  const menuItemSearchQuery = useMemo(
    () => ({
      time: timestampRef.current,
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
      operationId,
      isFutureOrder: false,
      restaurantStatus: 'ORDERABLE',
      brandUuid: '2d7a7540-f419-11e8-9737-5bbb692281b0',
      isNonRestaurantMerchant: false,
      merchantTypes: '',
      orderType: 'STANDARD',
      agent: false,
      weightedItemDataIncluded: true,
      task: 'MENU_ITEM_SEARCH',
      rpcVariant: 'PAGINATION_SUPPORTED',
      platform: 'WEB',
      restaurantId,
      menuItemId: 'None',
      dataType: 'CATEGORY_AND_MENU_ITEMS',
      searchQuery: filterInput,
      sortBy: 'CATEGORIES',
    }),
    [
      storageUserLocation.longitude,
      storageUserLocation.latitude,
      restaurantId,
      operationId,
      filterInput,
    ],
  )

  const {
    data: menuItemSearchData,
    isLoading: isMenuItemLoading,
    isError: isMenuItemSearchError,
  } = useMenuItemListing(menuItemSearchQuery, authData?.session_handle.access_token ?? '')

  useEffect(() => {
    setActiveIndex(menuItemSearchData?.object.data.content[0]?.entity?.category?.category_id ?? '')
  }, [menuItemSearchData?.object.data.content])

  const scrollToMenuItemList = (id: string) => {
    const section = menuItemRefs.current[id]
    setActiveIndex(id)
    if (section) {
      section.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }

  const handleCustomizeMenuItem = useCallback(
    (menuItemId: string): void => {
      setDialogState({type: 'customize'})
      setSelectedMenuItemId(menuItemId)
      setMenuItemId(menuItemId)
    },
    [setMenuItemId],
  )

  const handleDialogClose = useCallback(() => {
    setDialogState({type: 'closed'})
  }, [])

  const restaurantAvaibility = restaurantDetail?.restaurant_availability

  const handleSearchMenuItem = useCallback(() => {
    router.navigate({
      to: FoodGrubhubDetailRoute.fullPath,
      params: {restaurantId},
      search: {q: filterInput},
    })
  }, [router, restaurantId, filterInput])

  const handleSearchMenuItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchMenuItem()
    }
  }

  useEffect(() => {
    if (filterInput === '') {
      router.navigate({
        to: FoodGrubhubDetailRoute.fullPath,
        params: {restaurantId},
        search: {q: ''},
      })
    }
  }, [filterInput, restaurantId, router])

  const totalMenuItems = menuItemSearchData?.object.data.content.reduce((sum, item) => {
    return sum + (item.entity?.menu_items?.length ?? 0)
  }, 0)

  useEffect(() => {
    if (!isMenuItemLoading && totalMenuItems) {
      announce(`Found ${totalMenuItems} items matching your search: ${q}`, 'polite')
    }
  }, [isMenuItemLoading, totalMenuItems, q])

  return (
    <>
      {isMenuItemLoading ? (
        <Loading label={`Loading result for ${q}`} />
      ) : (
        <section className={style.PageContent}>
          <div className={style.CategoryContainer}>
            <div className={style.CategoryOptionContainer}>
              <div className={style.CategoryOption}>
                <CategoryIcon className={style.Icon} />
                <h2 className={style.Heading}>Categories</h2>
              </div>
              <div className={style.CategoryList} role='list'>
                {menuItemSearchData?.object.data.content.map(menuItemCategory => (
                  <button
                    key={menuItemCategory.entity?.category?.category_id}
                    type='button'
                    onClick={() => {
                      scrollToMenuItemList(menuItemCategory.entity?.category?.category_id ?? '')
                      document
                        .getElementById(
                          `menuItem-heading-${menuItemCategory.entity?.category?.category_id}`,
                        )
                        ?.focus()
                    }}
                    aria-label={`Go to ${menuItemCategory.entity?.category?.category_name} section`}
                    className={clsx(
                      style.Item,
                      activeIndex === menuItemCategory.entity?.category?.category_id &&
                        style.ItemActive,
                    )}
                  >
                    <VisuallyHidden role='heading' aria-level={3}>
                      {menuItemCategory.entity?.category?.category_name}
                    </VisuallyHidden>
                    {menuItemCategory.entity?.category?.category_name}
                  </button>
                ))}
              </div>
            </div>
            <div className={style.CategoryDetailContainer}>
              <div className={style.CategoryDetailFilter}>
                <div className={style.FilterContainer}>
                  <div className={style.FilterSearchContainer}>
                    <SearchIcon className={style.Icon} />
                    <input
                      type='text'
                      ref={filterInputRef}
                      placeholder='Filter result'
                      aria-label='Filter result'
                      value={filterInput}
                      onChange={e => {
                        setFilterInput(e.target.value)
                      }}
                      onKeyDown={e => {
                        handleSearchMenuItemKeyDown(e)
                      }}
                      className={style.SearchBox}
                    />
                    {filterInput.length > 0 && (
                      <button
                        type='button'
                        aria-label='Clear search'
                        onClick={() => {
                          setFilterInput('')
                          // filterInputRef.current?.focus()
                        }}
                        className={style.SearchBoxClearButtonContainer}
                      >
                        <CloseIcon className={style.SearchBoxClearButtonIcon} />
                      </button>
                    )}
                  </div>
                  <div className={style.FilterButtonContainer}>
                    <div
                      // onClick={() => {
                      //   handleChangeDeliveryType(false)
                      // }}
                      className={clsx(
                        style.FilterButton,
                        // !deliveryType && style.FilterButtonActive,
                      )}
                      aria-labelledby='delivery-time-label delivery-time-value'
                    >
                      <p className={style.ButtonTitle}>Delivery time</p>
                      <div className={style.ButtonValue}>
                        {restaurantAvaibility?.delivery_estimate} mins
                      </div>
                    </div>
                  </div>
                </div>
                <div className={style.CategoryOptionMobile}>
                  <div className={style.CategoryList} role='list' aria-label='List of menu items'>
                    {menuItemSearchData?.object.data.content.map(category => (
                      <button
                        key={category.entity?.category?.category_id}
                        type='button'
                        onClick={() => {
                          scrollToMenuItemList(category.entity?.category?.category_id ?? '')
                          document
                            .getElementById(
                              `menuItem-heading-${category.entity?.category?.category_id}`,
                            )
                            ?.focus()
                        }}
                        aria-label={`Go to ${category.entity?.category?.category_name} section`}
                        className={clsx(
                          style.Item,
                          activeIndex === category.entity?.category?.category_id &&
                            style.ItemActive,
                        )}
                      >
                        <VisuallyHidden role='heading' aria-level={3}>
                          {category.entity?.category?.category_name}
                        </VisuallyHidden>
                        {category.entity?.category?.category_name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {isMenuItemSearchError ? (
                <div className={style.MenuItemNotFound}>No items match your filter: {q}</div>
              ) : (
                <div className={style.CategoryDetailContent} role='list'>
                  {menuItemSearchData?.object.data.content.map(menuItem => (
                    <div
                      key={menuItem.entity?.category?.category_id}
                      ref={el => {
                        menuItemRefs.current[menuItem.entity?.category?.category_id ?? ''] = el
                      }}
                      data-id={menuItem.entity?.category?.category_id}
                      className={style.CategoryItem}
                      role='listitem'
                    >
                      <h3
                        id={`menuItem-heading-${menuItem.entity?.category?.category_id}`}
                        tabIndex={-1}
                        className={style.CategoryItemHeading}
                      >
                        <VisuallyHidden>Category:</VisuallyHidden>
                        {menuItem.entity?.category?.category_name}
                      </h3>
                      <div className={style.ItemList} role='list'>
                        {menuItem.entity?.menu_items?.map(item => {
                          const dollarPrice = (item.item_price?.delivery?.value ?? 0) / 100
                          const itemImagePathBuilder = `${item.media_image?.base_url}${item.media_image?.public_id}.${item.media_image?.format}`
                          return (
                            <div role='listitem' key={item.item_id} className={style.Item}>
                              <div className={style.ItemInfo}>
                                <div className={style.ItemInfoHeader}>
                                  <h4 className={style.ItemTitle}>{item.item_name}</h4>
                                  <div className={style.ItemDesc}>
                                    <VisuallyHidden>
                                      Description:{' '}
                                      {item.item_description?.length === 0 && 'Not available'}
                                    </VisuallyHidden>
                                    <ReadMoreString
                                      text={item.item_description ?? ''}
                                      maxChars={100}
                                    />
                                  </div>
                                </div>
                                <div className={style.ItemBasePrice}>
                                  <CategoryItemPrice price={dollarPrice} />
                                </div>
                              </div>
                              <div className={style.ItemImageContainer}>
                                {item.media_image ? (
                                  <img
                                    src={itemImagePathBuilder}
                                    alt=''
                                    loading='lazy'
                                    className={style.Image}
                                  />
                                ) : (
                                  <ImageDefaultLoader value='category' />
                                )}
                                <div className={style.AddingButtonContainer}>
                                  <button
                                    type='button'
                                    aria-label={`Customize the ${item.item_name} before adding to the cart`}
                                    className={style.AddingButton}
                                    onClick={() => {
                                      handleCustomizeMenuItem(item.item_id ?? '')
                                    }}
                                  >
                                    <PlusIcon className={style.AddingButtonIcon} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>{' '}
          <DialogTrigger
            isOpen={menuItemId !== '' || dialogState.type !== 'closed'}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setDialogState({type: 'closed'})
                setMenuItemId('')
              }
            }}
          >
            <MenuItemDetailDialog onClose={handleDialogClose} menuItemId={selectedMenuItemId} />
          </DialogTrigger>
        </section>
      )}
    </>
  )
}
